import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, isStaff, type Me } from './api.ts';
import { Backdrop } from './components/Backdrop.tsx';
import { Bell } from './components/Bell.tsx';
import { PushToggle } from './components/PushToggle.tsx';
import { ThemeToggle } from './components/ThemeToggle.tsx';
import { LoginPage } from './pages/Login.tsx';
import { CoursesPage } from './pages/Courses.tsx';
import { CoursePage } from './pages/Course.tsx';
import { LessonPage } from './pages/Lesson.tsx';
import { AttemptPage } from './pages/Attempt.tsx';
import { CertificatesPage } from './pages/Certificates.tsx';
import { LeaderboardPage } from './pages/Leaderboard.tsx';
import { MessagesPage } from './pages/Messages.tsx';
import { VerifyPage } from './pages/Verify.tsx';
import { InvitePage, ResetPasswordPage, VerifyEmailPage } from './pages/TokenPages.tsx';
import { AdminLayout } from './pages/admin/AdminLayout.tsx';
import { AdminCoursesPage } from './pages/admin/Courses.tsx';
import { CourseEditorPage } from './pages/admin/CourseEditor.tsx';
import { LessonEditorPage } from './pages/admin/LessonEditor.tsx';
import { AssignmentEditorPage } from './pages/admin/AssignmentEditor.tsx';
import { QuizEditorPage } from './pages/admin/QuizEditor.tsx';
import { QuestionBankPage } from './pages/admin/QuestionBank.tsx';
import { QueuePage } from './pages/admin/Queue.tsx';
import { StudentsPage } from './pages/admin/Students.tsx';
import { AdminCertificatesPage } from './pages/admin/Certificates.tsx';
import { ConversationsPage } from './pages/admin/Conversations.tsx';

/**
 * Two cabinets behind one door.
 *
 * The learner side is deliberately small: a list of courses, a course, a lesson. A student who
 * opens this should see what to do next without reading a manual — no navigation tree, no
 * dashboard widgets, no settings screen they will never touch. Everything a teacher needs lives
 * under /admin instead, so neither audience has to step over the other's controls.
 *
 * The role check here only decides what is shown. Every /admin route calls endpoints that check
 * the role again on the server, which is the check that actually matters.
 */

type Session = { state: 'loading' } | { state: 'anonymous' } | { state: 'signed-in'; me: Me };

/** Slow on purpose: a letter that arrives half a minute late costs nothing, a poll per second does. */
const UNREAD_POLL_MS = 30_000;

/**
 * The unread count behind the envelope.
 *
 * Polled rather than pushed, because a school of this size does not need a socket held open all
 * day to learn that one child asked a question. It also re-reads on every navigation, which is
 * what actually clears the badge: opening the conversation marks it read on the server, and coming
 * back from it asks again.
 */
function useUnread(staff: boolean): number {
  const [unread, setUnread] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let alive = true;

    const read = (): void => {
      const ask = staff ? api.authoring.unread() : api.myUnread();

      // A failed poll leaves the last known number alone. Flashing to zero on a dropped
      // connection would say "nothing new" — the one thing a badge must never say wrongly.
      ask.then((r) => alive && setUnread(r.unread)).catch(() => undefined);
    };

    read();
    const timer = setInterval(read, UNREAD_POLL_MS);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [staff, location.pathname]);

  return unread;
}

/**
 * The envelope.
 *
 * One component for both cabinets: the same thing means the same thing to a child and to their
 * teacher, only the door behind it differs. Silent when there is nothing — an envelope showing a
 * nought is noise that teaches people to stop looking at it.
 */
function MailBadge({ staff }: { staff: boolean }): React.JSX.Element {
  const unread = useUnread(staff);
  const to = staff ? '/admin/conversations' : '/messages';

  return (
    <Link
      to={to}
      className="mail"
      aria-label={unread > 0 ? `Сообщения: ${unread} непрочитанных` : 'Сообщения'}
      title={unread > 0 ? 'Есть непрочитанные сообщения' : 'Сообщения'}
    >
      {/* Drawn rather than typed: an emoji envelope is a different picture on every system. */}
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
        <path d="M3.2 6.6 12 13.2l8.8-6.6" />
      </svg>
      {unread > 0 ? (
        // Past ninety-nine the exact number stops being information and starts being a wide badge.
        <span className="mail__count">{unread > 99 ? '99+' : unread}</span>
      ) : null}
    </Link>
  );
}

function TopBar({
  me,
  staff,
  onSignOut,
}: {
  me: Me;
  staff: boolean;
  onSignOut: () => void;
}): React.JSX.Element {
  const org = me.organizations[0];

  return (
    <header className="topbar">
      <div className="shell topbar__inner">
        <Link to="/" className="brand">
          Junior<span>Prog</span>
        </Link>
        <div className="who">
          {staff ? (
            <>
              <Link to="/" className="toplink">
                Обучение
              </Link>
              <Link to="/admin" className="toplink">
                Админка
              </Link>
            </>
          ) : null}
          <Link to="/leaderboard" className="toplink">
            Рейтинг
          </Link>
          <ThemeToggle />
          <Bell />
          <PushToggle />
          <MailBadge staff={staff} />
          <span>
            {me.full_name}
            {org ? ` · ${org.name}` : ''}
          </span>
          <button type="button" className="btn btn--ghost btn--small" onClick={onSignOut}>
            Выйти
          </button>
        </div>
      </div>
    </header>
  );
}

export function App(): React.JSX.Element {
  const [session, setSession] = useState<Session>({ state: 'loading' });

  // One call decides everything: the cookie either resolves to a person or it does not.
  useEffect(() => {
    api
      .me()
      .then((me) => setSession({ state: 'signed-in', me }))
      .catch(() => setSession({ state: 'anonymous' }));
  }, []);

  const signOut = useCallback(() => {
    void api.logout().finally(() => setSession({ state: 'anonymous' }));
  }, []);

  if (session.state === 'loading') {
    return (
      <div className="center">
        <p className="muted">Загружаем…</p>
      </div>
    );
  }

  /*
   * Signed out, the app is a sign-in form — with one exception. The certificate check is the
   * address printed on a certificate and handed to an employer, so it has to answer for a visitor
   * who has no account here and never will. Behind the login gate, every printed certificate
   * would be unverifiable.
   */
  if (session.state === 'anonymous') {
    return (
      <BrowserRouter>
        <Backdrop />
        <Routes>
          <Route path="/verify/:number" element={<VerifyPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          {/* Everything below arrives as a link in a letter, and its reader has no session. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route
            path="*"
            element={<LoginPage onSignedIn={(me) => setSession({ state: 'signed-in', me })} />}
          />
        </Routes>
      </BrowserRouter>
    );
  }

  const staff = isStaff(session.me);

  return (
    <BrowserRouter>
      <Backdrop />
      <TopBar me={session.me} staff={staff} onSignOut={signOut} />
      <main className="shell">
        <Routes>
          <Route path="/" element={<CoursesPage />} />
          <Route path="/courses/:courseId" element={<CoursePage />} />
          <Route path="/lessons/:lessonId" element={<LessonPage />} />
          <Route path="/attempts/:attemptId" element={<AttemptPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          {/* Public, but signed-in visitors follow the same link from their own certificates. */}
          <Route path="/verify/:number" element={<VerifyPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          {/* Somebody already signed in may still follow a link from a letter. */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/invite" element={<InvitePage />} />

          {/* A student who guesses the URL is sent home rather than shown an empty shell. */}
          {staff ? (
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminCoursesPage />} />
              <Route path="courses/:courseId" element={<CourseEditorPage />} />
              <Route path="lessons/:lessonId" element={<LessonEditorPage />} />
              <Route path="assignments/:assignmentId" element={<AssignmentEditorPage />} />
              <Route path="quizzes/:quizId" element={<QuizEditorPage />} />
              <Route path="questions" element={<QuestionBankPage />} />
              <Route path="queue" element={<QueuePage />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="certificates" element={<AdminCertificatesPage />} />
              <Route path="conversations" element={<ConversationsPage />} />
            </Route>
          ) : null}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </BrowserRouter>
  );
}
